import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { registerUser, loginUser, loginWithGoogle } from '../firebase/auth';
import { getUserRole } from '../firebase/auth';
import { getUserData } from '../firebase/database';
import { useAuthStore } from '../store';
import { Spinner } from '../components/common/Loader';
import SEO from '../components/common/SEO';
import toast from 'react-hot-toast';

const InputField = ({ label, type='text', value, onChange, placeholder, icon }) => (
  <div>
    <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wider">{label}</label>
    <div className="relative">
      {icon && (
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none w-4 h-4 flex items-center justify-center">
          {icon}
        </div>
      )}
      <input type={type} value={value} onChange={onChange} placeholder={placeholder}
        className={`input-field ${icon ? 'pl-10' : ''}`} required />
    </div>
  </div>
);

const AuthPage = () => {
  const [mode, setMode] = useState('login');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name:'', email:'', password:'', confirm:'' });
  const { setUser, setUserRole, setUserData } = useAuthStore();
  const navigate = useNavigate();
  const update = (f,v) => setForm(p=>({...p,[f]:v}));

  const afterLogin = async (user) => {
    const role = await getUserRole(user.uid);
    const data = await getUserData(user.uid);
    setUser(user); setUserRole(role); setUserData(data);
    toast.success(`Welcome${user.displayName?', '+user.displayName.split(' ')[0]:''}!`);
    navigate(role==='admin'?'/admin':'/');
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      if (mode==='register') {
        if (form.password!==form.confirm) { toast.error('Passwords do not match'); return; }
        if (form.password.length<6) { toast.error('Password must be at least 6 characters'); return; }
        const user = await registerUser(form.email, form.password, form.name);
        await afterLogin(user);
      } else {
        const user = await loginUser(form.email, form.password);
        await afterLogin(user);
      }
    } catch (err) {
      const msgs = { 'auth/user-not-found':'No account with this email', 'auth/wrong-password':'Incorrect password', 'auth/email-already-in-use':'Email already registered', 'auth/invalid-email':'Invalid email', 'auth/too-many-requests':'Too many attempts. Try later.' };
      toast.error(msgs[err.code]||'Authentication failed');
    } finally { setLoading(false); }
  };

  const handleGoogle = async () => {
    setLoading(true);
    try { const user = await loginWithGoogle(); await afterLogin(user); }
    catch { toast.error('Google sign-in failed'); }
    finally { setLoading(false); }
  };

  const UserIcon = () => <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>;
  const MailIcon = () => <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>;
  const LockIcon = () => <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/></svg>;
  const EyeIcon = () => <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>;
  const EyeOffIcon = () => <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"/></svg>;

  return (
    <>
      <SEO title={mode==='login'?'Sign In':'Create Account'}/>
      <div className="min-h-screen bg-[#f8f9fc] flex">
        {/* Left */}
        <div className="hidden lg:flex w-1/2 bg-[#0f2347] relative overflow-hidden flex-col items-center justify-center p-16">
          <div className="absolute inset-0 diagonal-bg-light opacity-40"/>
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#e63946]/10 rounded-full translate-x-1/3 -translate-y-1/3"/>
          <div className="relative text-center">
            <img src="/logo.png" alt="SitSmart" className="h-24 w-auto object-contain mb-3 bg-white px-4 py-2 rounded-xl" />
            <p className="text-white/50 text-sm tracking-widest uppercase mb-12">Smart Seating For Every Home</p>
            <div className="space-y-5 text-left max-w-xs">
              {['Premium quality furniture','Fast delivery across Pakistan','Secure payments & easy returns','Exclusive member discounts'].map((t,i)=>(
                <motion.div key={t} initial={{opacity:0,x:-20}} animate={{opacity:1,x:0}} transition={{delay:0.2+i*0.1}}
                  className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-[#e63946] rounded-full flex items-center justify-center flex-shrink-0">
                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>
                  </div>
                  <span className="text-white/80 text-sm">{t}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* Right */}
        <div className="flex-1 flex items-center justify-center p-6">
          <motion.div initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} className="w-full max-w-md">
            <div className="lg:hidden text-center mb-8">
              <Link to="/"><img src="/logo.png" alt="SitSmart" className="h-12 w-auto object-contain mx-auto" /></Link>
            </div>
            <div className="bg-white shadow-xl p-8">
              {/* Tabs */}
              <div className="flex mb-8 border-b border-gray-200">
                {['login','register'].map(m=>(
                  <button key={m} onClick={()=>setMode(m)}
                    className={`flex-1 pb-3 text-sm font-bold capitalize transition-all border-b-2 -mb-px ${mode===m?'border-[#1a3a6e] text-[#1a3a6e]':'border-transparent text-gray-400 hover:text-gray-600'}`}>
                    {m==='login'?'Sign In':'Create Account'}
                  </button>
                ))}
              </div>

              <AnimatePresence mode="wait">
                <motion.form key={mode} initial={{opacity:0,x:10}} animate={{opacity:1,x:0}} exit={{opacity:0}}
                  onSubmit={handleSubmit} className="space-y-4">
                  {mode==='register'&&(
                    <InputField label="Full Name" value={form.name} onChange={e=>update('name',e.target.value)}
                      placeholder="Muhammad Ahmed" icon={<UserIcon/>}/>
                  )}
                  <InputField label="Email Address" type="email" value={form.email} onChange={e=>update('email',e.target.value)}
                    placeholder="email@example.com" icon={<MailIcon/>}/>
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wider">Password</label>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none w-4 h-4 flex items-center justify-center"><LockIcon/></div>
                      <input type={showPass?'text':'password'} value={form.password} onChange={e=>update('password',e.target.value)}
                        placeholder="Min 6 characters" className="input-field pl-10 pr-10" required/>
                      <button type="button" onClick={()=>setShowPass(!showPass)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 w-4 h-4 flex items-center justify-center">
                        {showPass?<EyeOffIcon/>:<EyeIcon/>}
                      </button>
                    </div>
                  </div>
                  {mode==='register'&&(
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wider">Confirm Password</label>
                      <div className="relative">
                        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none w-4 h-4 flex items-center justify-center"><LockIcon/></div>
                        <input type="password" value={form.confirm} onChange={e=>update('confirm',e.target.value)}
                          placeholder="Repeat password" className="input-field pl-10" required/>
                      </div>
                    </div>
                  )}
                  {mode==='login'&&(
                    <div className="text-right">
                      <Link to="/forgot-password" className="text-xs text-[#1a3a6e] hover:underline">Forgot Password?</Link>
                    </div>
                  )}
                  <button type="submit" disabled={loading}
                    className="btn-primary w-full flex items-center justify-center gap-2 py-3.5">
                    {loading?<Spinner size="sm" color="white"/>:
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3"/></svg>}
                    {loading?'Please wait...':(mode==='login'?'Sign In':'Create Account')}
                  </button>
                </motion.form>
              </AnimatePresence>

              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200"/></div>
                <div className="relative text-center"><span className="bg-white px-4 text-xs text-gray-400">OR CONTINUE WITH</span></div>
              </div>
              <button onClick={handleGoogle} disabled={loading}
                className="w-full flex items-center justify-center gap-3 border border-gray-200 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Google
              </button>
              <p className="text-center text-xs text-gray-500 mt-6">
                By continuing, you agree to our{' '}
                <Link to="/terms" className="text-[#1a3a6e] hover:underline">Terms</Link> &{' '}
                <Link to="/privacy-policy" className="text-[#1a3a6e] hover:underline">Privacy Policy</Link>
              </p>
            </div>
            <p className="text-center text-sm text-gray-500 mt-4">
              <Link to="/" className="text-[#1a3a6e] hover:underline">← Back to Home</Link>
            </p>
          </motion.div>
        </div>
      </div>
    </>
  );
};
export default AuthPage;
